/**
 * UpgradeRenewModal — Shows pricing tiers and activates a selected tier instantly.
 *
 * Triggered by `LicenseContext.openUpgradeModal()` (banner + ribbon CTA).
 * Tiers are loaded from /api/license/pricing-tiers (MSSQL → seed fallback).
 *
 * Flow (no payment gate — extend later):
 *   1. User picks a tier → POST /api/license/activate-tier
 *   2. License auto-refreshes via LicenseContext.refresh(clientId)
 */
import React, { useEffect, useState } from 'react';
import { X, Check, ShieldCheck, Sparkles, Loader } from 'lucide-react';
import { useLicense } from '@/context/LicenseContext';
import { API_BASE, getToken } from '@/lib/api';

const fetchJSON = (path, opts = {}) =>
  fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  }).then(r => r.json());

export default function UpgradeRenewModal({ theme = 'light' }) {
  const lic = useLicense();
  const { upgradeOpen, closeUpgradeModal, availableTiers, tier: currentTier, clientId, refresh } = lic;
  const isDark = theme === 'dark';

  const [tiers, setTiers]   = useState(availableTiers || []);
  const [picked, setPicked] = useState(null);
  const [step, setStep]     = useState('pick'); // pick | activating | success | error
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!upgradeOpen) return;
    if (tiers.length === 0) {
      fetchJSON('/api/license/pricing-tiers').then(d => setTiers(d.tiers || []));
    }
    setStep('pick'); setPicked(null); setError('');
  }, [upgradeOpen, tiers.length]);

  if (!upgradeOpen) return null;

  const activateTier = async (t) => {
    if (!clientId) { setError('No client ID in session. Please log in again.'); setStep('error'); return; }
    setPicked(t);
    setStep('activating');
    setError('');
    try {
      const r = await fetchJSON('/api/license/activate-tier', {
        method: 'POST',
        body: JSON.stringify({ clientId, tierCode: t.code }),
      });
      if (!r.success) throw new Error(r.error || 'Activation failed');
      setStep('success');
      if (clientId) setTimeout(() => refresh(clientId), 400);
    } catch (e) {
      setError(e.message || 'Activation failed');
      setStep('error');
    }
  };

  return (
    <div data-testid="upgrade-modal" style={{
      position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', padding: 16,
    }}>
      <div style={{
        width: '100%', maxWidth: 880, maxHeight: '90vh', overflow: 'auto',
        background: isDark ? '#0f0f10' : '#fff', borderRadius: 12,
        border: isDark ? '1px solid #2a2a2a' : '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: isDark ? '1px solid #2a2a2a' : '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Sparkles size={20} style={{ color: '#3b82f6' }} />
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: isDark ? '#fff' : '#0f172a' }}>
                {step === 'pick' ? 'Upgrade / Renew License' : step === 'success' ? 'License Activated' : 'Activating…'}
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>
                Current: <strong>{currentTier}</strong> · {lic.compName || 'RetailCLIQ ERP'}
              </p>
            </div>
          </div>
          <button data-testid="upgrade-modal-close" onClick={closeUpgradeModal}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: isDark ? '#94a3b8' : '#64748b', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {step === 'pick' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              {tiers.map(t => {
                const isCurrent = t.code === currentTier;
                return (
                  <div key={t.code} data-testid={`tier-card-${t.code}`}
                    style={{
                      background: isDark ? '#1a1a1a' : '#f8fafc',
                      border: isCurrent ? '2px solid #3b82f6' : isDark ? '1px solid #2a2a2a' : '1px solid #e2e8f0',
                      borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
                      position: 'relative',
                    }}>
                    {t.badge && (
                      <span style={{ position: 'absolute', top: 8, right: 8, background: '#f59e0b', color: '#0f172a', fontSize: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{t.badge}</span>
                    )}
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: isDark ? '#fff' : '#0f172a' }}>{t.name}</h3>
                    <p style={{ margin: 0, fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', minHeight: 28 }}>{t.tagline}</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: 22, fontWeight: 900, color: isDark ? '#fff' : '#0f172a' }}>
                        {t.billingCycle === 'custom' ? 'Custom' : t.price === 0 ? 'Free' : `${t.currency === 'INR' ? '₹' : t.currency}${t.price.toLocaleString('en-IN')}`}
                      </span>
                      {t.price > 0 && t.billingCycle !== 'custom' && <span style={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}>/{t.billingCycle === 'monthly' ? 'mo' : t.billingCycle}</span>}
                    </div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(t.features || []).slice(0, 4).map((f, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 10, color: isDark ? '#cbd5e1' : '#334155' }}>
                          <Check size={11} style={{ color: '#10b981', marginTop: 2, flexShrink: 0 }} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid={`tier-pick-${t.code}`}
                      disabled={isCurrent}
                      onClick={() => activateTier(t)}
                      style={{
                        marginTop: 'auto',
                        background: isCurrent ? (isDark ? '#1f2937' : '#e2e8f0') : '#3b82f6',
                        color: isCurrent ? (isDark ? '#94a3b8' : '#64748b') : '#fff',
                        border: 'none', borderRadius: 6, padding: '8px 12px',
                        fontSize: 11, fontWeight: 700, cursor: isCurrent ? 'default' : 'pointer',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>
                      {isCurrent ? 'Current Tier' : t.billingCycle === 'custom' ? 'Contact Sales' : 'Activate'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {step === 'activating' && (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Loader size={36} style={{ color: '#3b82f6', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
              <p style={{ margin: 0, fontSize: 13, color: isDark ? '#94a3b8' : '#64748b' }}>
                Activating <strong>{picked?.name}</strong> tier…
              </p>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {step === 'success' && (
            <div style={{ textAlign: 'center', padding: 36 }}>
              <ShieldCheck size={44} style={{ color: '#10b981', margin: '0 auto 14px' }} />
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: isDark ? '#fff' : '#0f172a' }}>License Activated</h3>
              <p style={{ margin: '0 0 6px', fontSize: 13, color: isDark ? '#94a3b8' : '#64748b' }}>
                Your <strong>{picked?.name}</strong> tier is now live.
              </p>
              <p style={{ margin: 0, fontSize: 11, color: isDark ? '#475569' : '#94a3b8' }}>
                License updated in the master registry and refreshed in your session.
              </p>
              <button data-testid="upgrade-done-btn" onClick={closeUpgradeModal}
                style={{ marginTop: 24, background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 28px', fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Done
              </button>
            </div>
          )}

          {step === 'error' && (
            <div style={{ textAlign: 'center', padding: 36 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 800, color: isDark ? '#fca5a5' : '#dc2626' }}>Activation Failed</h3>
              <p style={{ margin: 0, fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>{error}</p>
              <button onClick={() => setStep('pick')} style={{ marginTop: 16, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
